import ImageRepository from '@app/repositories/imageRepository';
import CategoryRepository from '@app/repositories/categoryRepository';
import EntityTypeRepository from '@app/repositories/entityTypeRepository';
import store from '@app/store';
import { Category, GalleryItem } from '@app/models';

export default class EntityType {
  id: string;

  userId: string;

  universeId: string;

  name: string;

  public: boolean;

  pluralName: string;

  position: number;

  parentId: string;

  galleryItem?: GalleryItem;

  entityDefaultImage?: GalleryItem;

  categories: Array<Category>;

  constructor(obj: any) {
    this.id = obj.id;
    this.userId = obj.userId;
    this.universeId = obj.universeId;
    this.name = obj.name;
    this.pluralName = obj.pluralName;
    this.position = obj.position;
    this.parentId = obj.parentId;
    this.public = obj.public;
    this.galleryItem = obj.galleryItem ? new GalleryItem(obj.galleryItem) : undefined;
    this.entityDefaultImage = obj.entityDefaultImage
      ? new GalleryItem(obj.entityDefaultImage)
      : undefined;
    this.categories = obj.categories?.map(
      (category: any) => new Category(category),
    );
  }

  async rename(newName: string) {
    this.name = newName;
    return EntityTypeRepository.update(
      {
        name: this.name,
        id: this.id,
      },
    ).then((updatedInfo) => {
      this.pluralName = updatedInfo.pluralName;
    });
  }

  renamePlural(newName: string) {
    this.pluralName = newName;
    return EntityTypeRepository.update(
      {
        pluralName: this.pluralName,
        id: this.id,
      },
    );
  }

  async createCategory(category: Partial<Category>) {
    return CategoryRepository.create(this.id, category)
      .then((createdCategory) => {
        this.categories.push(createdCategory);
        return createdCategory;
      });
  }

  async addImage(file: File) {
    return ImageRepository.setEntityTypeImage(this.id, file).then((response) => {
      this.galleryItem = new GalleryItem(response);
    });
  }

  async addImageByUrl(url: string): Promise<void> {
    return ImageRepository.setEntityTypeImageByUrl(this.id, url).then((response) => {
      this.galleryItem = new GalleryItem(response);
    });
  }

  getThumbnailUrl(): string {
    if (this.galleryItem) {
      return `${COW_USER_IMAGE_URL}/thumbnails/${this.galleryItem.id}.${this.galleryItem.thumbnailExtension}`;
    }

    return '/images/default.jpg';
  }

  getImageUrl(): string | undefined {
    if (this.galleryItem) {
      return `${COW_USER_IMAGE_URL}/${this.galleryItem.id}.${this.galleryItem.extension}`;
    }
  }

  getImageUrls(): Array<string> {
    const image = this.getImageUrl();
    if (image) {
      return [image];
    }

    return [];
  }

  usesDefaultImage(): boolean {
    return !this.galleryItem;
  }

  async addEntityDefaultImage(file: File): Promise<void> {
    const response = await ImageRepository.setEntityTypeEntityDefaultImage(this.id, file);
    this.entityDefaultImage = new GalleryItem(response);
  }

  async addEntityDefaultImageByUrl(url: string): Promise<void> {
    const response = await ImageRepository.setEntityTypeEntityDefaultImageByUrl(this.id, url);
    this.entityDefaultImage = new GalleryItem(response);
  }

  async deleteEntityDefaultImage(): Promise<void> {
    if (!this.entityDefaultImage) return;
    await ImageRepository.deleteEntityTypeEntityDefaultImage(this.id);
    this.entityDefaultImage = undefined;
  }

  async deleteImage(): Promise<void> {
    if (this.galleryItem) {
      const deletedImage = this.galleryItem;
      return ImageRepository.deleteEntityTypeImage(this.id, this.galleryItem.id).then(() => {
        this.galleryItem = undefined;
        store.state.toasts.push(
          {
            componentName: 'UndoDeleteToast',
            key: deletedImage.id,
            message: `Deleted default image for "${this.pluralName}"`,
            undoDelete: async() => {
              await ImageRepository.undoDeleteEntityTypeImage(this.id, deletedImage.id);
              this.galleryItem = deletedImage;
            },
          },
        );
      });
    }
  }

  getCategory(categoryId: string): Category | undefined {
    return this.categories.find((category: Category) => category.id === categoryId);
  }

  async deleteCategory(category: Category): Promise<void> {
    return CategoryRepository.delete(category.id).then(() => {
      const index = this.categories.findIndex((candidateCategory) => candidateCategory.id === category.id);
      this.categories.splice(index, 1);
      store.state.toasts.push(
        {
          componentName: 'UndoDeleteToast',
          key: category.id,
          message: `Deleted category "${category.name}" in ${this.pluralName}`,
          undoDelete: async() => {
            await CategoryRepository.undoDelete(category.id);

            let insertIndex = 0;
            for (const existingCategory of this.categories) {
              if (existingCategory.position > category.position) {
                break;
              }
              insertIndex += 1;
            }

            this.categories.splice(insertIndex, 0, category);
          },
        },
      );
    });
  }

  getField(fieldId: string) {
    for (const category of this.categories) {
      for (const field of category.fields) {
        if (field.id === fieldId) {
          return field;
        }
      }
    }
    return undefined;
  }
}
